import { passHeaderSvg, svgToDataUrl } from "@/lib/cardArt";
import { PASS_ACCENT, formatDate, formatTime, splitTitle } from "@/lib/event";

const W = 1080;
const H = 1620;
const NAVY = "#142344";
const PINK = "#D81B60";
const PINK_DEEP = "#A3134A";
const CREAM = "#FFF7E8";
const INSET = 34;

function wrap(ctx, text, maxW) {
  const words = String(text).split(/\s+/).filter(Boolean);
  const lines = [];
  let line = "";
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width <= maxW) line = test;
    else {
      if (line) lines.push(line);
      line = w;
      while (ctx.measureText(line).width > maxW && line.length > 1) {
        let cut = line.length - 1;
        while (cut > 1 && ctx.measureText(line.slice(0, cut)).width > maxW) cut--;
        lines.push(line.slice(0, cut));
        line = line.slice(cut);
      }
    }
  }
  if (line) lines.push(line);
  return lines;
}

function spaced(ctx, text, x, y, spacing) {
  const chars = [...text];
  const total = chars.reduce((s, c) => s + ctx.measureText(c).width, 0) + spacing * (chars.length - 1);
  let cx = x - total / 2;
  ctx.textAlign = "left";
  for (const c of chars) {
    ctx.fillText(c, cx, y);
    cx += ctx.measureText(c).width + spacing;
  }
  ctx.textAlign = "center";
}

const loadImg = (src) =>
  new Promise((res, rej) => {
    const img = new Image();
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = src;
  });

function fitFont(ctx, text, family, start, maxW) {
  let size = start;
  ctx.font = `${family.replace("{s}", size)}`;
  while (ctx.measureText(text).width > maxW && size > 20) {
    size -= 2;
    ctx.font = `${family.replace("{s}", size)}`;
  }
  return size;
}

function stamp(ctx, x, y, hosts) {
  const words = (hosts || "Admin").trim().split(/\s+/);
  const main = words.pop().toUpperCase();
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(-0.22);
  ctx.strokeStyle = PINK;
  ctx.fillStyle = PINK;
  ctx.lineWidth = 6;
  ctx.beginPath();
  ctx.arc(0, 0, 108, 0, Math.PI * 2);
  ctx.stroke();
  ctx.lineWidth = 2.5;
  ctx.setLineDash([6, 6]);
  ctx.beginPath();
  ctx.arc(0, 0, 92, 0, Math.PI * 2);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.textAlign = "center";
  ctx.font = '400 22px "Anton"';
  const pre = words.length ? wrap(ctx, words.join(" ").toUpperCase(), 130).slice(0, 2) : [];
  pre.forEach((l, i) => ctx.fillText(l, 0, -48 + i * 25 + (pre.length === 1 ? 14 : 0)));
  ctx.font = '400 40px "Anton"';
  fitFont(ctx, main, '400 {s}px "Anton"', 40, 150);
  ctx.fillText(main, 0, pre.length ? 22 : -6);
  ctx.font = '400 26px "Anton"';
  ctx.fillText("APPROVED", 0, pre.length ? 52 : 32);
  ctx.font = '700 16px "DM Sans"';
  ctx.fillText("★ ★ ★", 0, pre.length ? 74 : 62);
  ctx.restore();
}

function farmaanStamp(ctx, x, y) {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(0.24);
  ctx.fillStyle = NAVY;
  ctx.beginPath();
  ctx.arc(8, 8, 128, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#FFC83D";
  ctx.strokeStyle = NAVY;
  ctx.lineWidth = 7;
  ctx.beginPath();
  ctx.arc(0, 0, 128, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  ctx.strokeStyle = PINK;
  ctx.lineWidth = 4;
  ctx.setLineDash([12, 9]);
  ctx.beginPath();
  ctx.arc(0, 0, 110, 0, Math.PI * 2);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.textAlign = "center";
  ctx.fillStyle = NAVY;
  ctx.font = '400 50px "Anton"';
  ctx.fillText("USOOL", 0, -6);
  ctx.fillStyle = PINK_DEEP;
  ctx.font = '400 36px "Anton"';
  ctx.fillText("MANZOOR!", 0, 38);
  ctx.fillStyle = NAVY;
  ctx.font = '800 26px "DM Sans"';
  ctx.fillText("✓ ✓ ✓", 0, 76);
  ctx.restore();
}

export async function renderInviteCanvas({ ev, name, mood, accepted = false }) {
  const accent = PASS_ACCENT;
  await Promise.all([
    document.fonts.load('400 120px "Anton"'),
    document.fonts.load('italic 400 120px "Instrument Serif"'),
    document.fonts.load('500 30px "DM Sans"'),
    document.fonts.load('700 30px "DM Sans"'),
  ]).catch(() => {});
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = 2200;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = CREAM;
  ctx.fillRect(0, 0, W, 2200);

  ctx.save();
  ctx.beginPath();
  ctx.roundRect(INSET, INSET, W - INSET * 2, H - INSET * 2, 44);
  ctx.clip();
  const art = await loadImg(svgToDataUrl(passHeaderSvg(accent.hex)));
  ctx.drawImage(art, INSET, INSET, W - INSET * 2, ((W - INSET * 2) * 500) / 1080);
  ctx.restore();
  if (accepted) farmaanStamp(ctx, 880, 400);
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  let y = 590;
  ctx.fillStyle = PINK_DEEP;
  ctx.font = '700 26px "DM Sans"';
  spaced(ctx, (ev.opening_eyebrow || "PRIVATE PARTY • INVITE ONLY").toUpperCase(), W / 2, y, 5);

  const { l1, l2, last } = splitTitle(ev.title);
  y += 128;
  ctx.fillStyle = NAVY;
  fitFont(ctx, l1.toUpperCase(), '400 {s}px "Anton"', 124, 900);
  ctx.fillText(l1.toUpperCase(), W / 2, y);
  y += 124;
  const left = l2 ? `${l2.toUpperCase()} ` : "";
  ctx.font = '400 124px "Anton"';
  const lw = ctx.measureText(left).width;
  ctx.font = 'italic 400 140px "Instrument Serif"';
  const rw = ctx.measureText(last).width;
  let sx = W / 2 - (lw + rw) / 2;
  ctx.textAlign = "left";
  ctx.font = '400 124px "Anton"';
  ctx.fillText(left, sx, y);
  ctx.fillStyle = PINK;
  ctx.font = 'italic 400 140px "Instrument Serif"';
  ctx.fillText(last, sx + lw, y);
  ctx.textAlign = "center";

  y += 74;
  ctx.fillStyle = NAVY;
  ctx.font = '700 24px "DM Sans"';
  spaced(ctx, "ADMIT", W / 2, y, 8);
  y += 82;
  ctx.font = 'italic 400 88px "Instrument Serif"';
  let nameLines = wrap(ctx, name || "Our favourite guest", 880);
  if (nameLines.length > 2) {
    ctx.font = 'italic 400 70px "Instrument Serif"';
    nameLines = wrap(ctx, name, 900).slice(0, 3);
  }
  const lh = nameLines.length > 2 ? 70 : 84;
  for (const l of nameLines) {
    ctx.fillText(l, W / 2, y);
    y += lh;
  }

  y += 4;
  ctx.fillStyle = NAVY;
  ctx.fillRect(110, y - 40, W - 220, 3);
  const colY = y + 14;
  ctx.font = '700 22px "DM Sans"';
  ctx.fillStyle = PINK_DEEP;
  spaced(ctx, "KAB?", W / 2 - 220, colY, 5);
  spaced(ctx, "KITNE BAJE?", W / 2 + 220, colY, 5);
  ctx.fillStyle = NAVY;
  ctx.font = '400 46px "Anton"';
  ctx.fillText(formatDate(ev.date).toUpperCase().replace(/^(\w+),/, (m, d) => d.slice(0, 3) + ","), W / 2 - 220, colY + 58, 420);
  ctx.fillText(formatTime(ev.start_time), W / 2 + 220, colY + 58);
  ctx.fillRect(W / 2 - 1.5, colY - 26, 3, 100);
  y = colY + 120;
  ctx.font = '700 22px "DM Sans"';
  ctx.fillStyle = PINK_DEEP;
  spaced(ctx, "KAHAN?", W / 2, y, 5);
  ctx.fillStyle = NAVY;
  fitFont(ctx, ev.venue_title.toUpperCase(), '400 {s}px "Anton"', 46, 860);
  ctx.fillText(ev.venue_title.toUpperCase(), W / 2, y + 56);

  const perf = Math.max(y + 100, 1240);
  ctx.strokeStyle = NAVY;
  ctx.lineWidth = 4;
  ctx.setLineDash([16, 14]);
  ctx.beginPath();
  ctx.moveTo(INSET + 40, perf);
  ctx.lineTo(W - INSET - 40, perf);
  ctx.stroke();
  ctx.setLineDash([]);

  const boxY = perf + 44;
  const boxW = 600;
  ctx.fillStyle = accent.soft;
  ctx.strokeStyle = NAVY;
  ctx.lineWidth = 5;
  ctx.beginPath();
  ctx.roundRect(100, boxY, boxW, 210, 26);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = accent.hex;
  ctx.beginPath();
  ctx.roundRect(100, boxY, 26, 210, [26, 0, 0, 26]);
  ctx.fill();
  ctx.textAlign = "left";
  ctx.fillStyle = PINK_DEEP;
  ctx.font = '700 22px "DM Sans"';
  ctx.fillText(mood ? "FESTIVE MOOD" : "THE VIBE", 156, boxY + 52);
  ctx.fillStyle = NAVY;
  const moodTitle = (mood ? mood.name : ev.opening_line || ev.tagline).toUpperCase();
  fitFont(ctx, moodTitle, '400 {s}px "Anton"', 44, boxW - 90);
  ctx.fillText(moodTitle, 156, boxY + 108);
  ctx.font = '500 25px "DM Sans"';
  const sub = mood ? mood.line : "";
  if (sub) ctx.fillText(sub, 156, boxY + 148, boxW - 80);
  ctx.textAlign = "center";
  stamp(ctx, 850, boxY + 104, ev.host_names);

  const FH = Math.max(H, boxY + 210 + 70 + INSET);
  const out = document.createElement("canvas");
  out.width = W;
  out.height = FH;
  const o = out.getContext("2d");
  o.drawImage(canvas, 0, 0);
  o.strokeStyle = NAVY;
  o.lineWidth = 8;
  o.beginPath();
  o.roundRect(INSET, INSET, W - INSET * 2, FH - INSET * 2, 44);
  o.stroke();
  for (const x of [INSET, W - INSET]) {
    o.fillStyle = CREAM;
    o.beginPath();
    o.arc(x, perf, 30, 0, Math.PI * 2);
    o.fill();
    o.stroke();
  }
  o.fillStyle = CREAM;
  o.fillRect(0, perf - 40, INSET - 4, 80);
  o.fillRect(W - INSET + 4, perf - 40, INSET, 80);
  o.fillRect(0, FH - INSET + 4, W, INSET);
  return out;
}

export const canvasToBlob = (canvas) => new Promise((res) => canvas.toBlob(res, "image/png"));
